import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  IconButton,
  TextField,
  Switch,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import AddIcon from "@material-ui/icons/Add";
import EventAvailableIcon from "@material-ui/icons/EventAvailable";
import BeachAccessIcon from "@material-ui/icons/BeachAccess";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";

// Algoritmo oficial Computus para calcular a Páscoa e feriados móveis do Brasil para qualquer ano
const calculateEaster = (year) => {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
};

const addDays = (date, days) => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

const toISODate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

export const getBrazilHolidaysForYear = (year) => {
  const easter = calculateEaster(year);
  const carnavalSeg = addDays(easter, -48);
  const carnavalQua = addDays(easter, -46);
  const sextaSanta = addDays(easter, -2);
  const corpusChristi = addDays(easter, 60);

  return [
    { id: `ano_novo_${year}`, name: "Confraternização Universal (Ano Novo)", startDate: `${year}-01-01`, endDate: `${year}-01-01`, active: true },
    { id: `carnaval_${year}`, name: "Carnaval & Recesso de Cinzas", startDate: toISODate(carnavalSeg), endDate: toISODate(carnavalQua), active: true },
    { id: `sexta_santa_${year}`, name: "Sexta-Feira Santa (Paixão de Cristo)", startDate: toISODate(sextaSanta), endDate: toISODate(sextaSanta), active: true },
    { id: `pascoa_${year}`, name: "Páscoa", startDate: toISODate(easter), endDate: toISODate(easter), active: true },
    { id: `tiradentes_${year}`, name: "Tiradentes", startDate: `${year}-04-21`, endDate: `${year}-04-21`, active: true },
    { id: `dia_trabalho_${year}`, name: "Dia do Trabalho", startDate: `${year}-05-01`, endDate: `${year}-05-01`, active: true },
    { id: `corpus_christi_${year}`, name: "Corpus Christi", startDate: toISODate(corpusChristi), endDate: toISODate(corpusChristi), active: true },
    { id: `independencia_${year}`, name: "Independência do Brasil", startDate: `${year}-09-07`, endDate: `${year}-09-07`, active: true },
    { id: `ns_aparecida_${year}`, name: "Nossa Senhora Aparecida (Padroeira do Brasil)", startDate: `${year}-10-12`, endDate: `${year}-10-12`, active: true },
    { id: `finados_${year}`, name: "Finados", startDate: `${year}-11-02`, endDate: `${year}-11-02`, active: true },
    { id: `proclamacao_${year}`, name: "Proclamação da República", startDate: `${year}-11-15`, endDate: `${year}-11-15`, active: true },
    { id: `consciencia_negra_${year}`, name: "Dia da Consciência Negra", startDate: `${year}-11-20`, endDate: `${year}-11-20`, active: true },
    { id: `natal_${year}`, name: "Véspera e Natal", startDate: `${year}-12-24`, endDate: `${year}-12-25`, active: true },
    { id: `ano_novo_vespera_${year}`, name: "Véspera de Ano Novo", startDate: `${year}-12-31`, endDate: `${year}-12-31`, active: true }
  ];
};

const getSaoPauloToday = () => {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Sao_Paulo"
  }).format(new Date());
};

const formatDateBR = (isoDate) => {
  if (!isoDate) return "";
  const [year, month, day] = isoDate.split("-");
  return `${day}/${month}/${year}`;
};

const WEEKDAY_NAMES_PT = [
  "Domingo",
  "Segunda-feira",
  "Terça-feira",
  "Quarta-feira",
  "Quinta-feira",
  "Sexta-feira",
  "Sábado"
];

const WEEKDAY_SHORT_PT = [
  "Dom",
  "Seg",
  "Ter",
  "Qua",
  "Qui",
  "Sex",
  "Sáb"
];

const getWeekdayLabel = (startDateStr, endDateStr) => {
  if (!startDateStr) return "";
  const [sy, sm, sd] = startDateStr.split("-").map(Number);
  const startDate = new Date(sy, sm - 1, sd);
  const startDay = startDate.getDay();

  if (!endDateStr || endDateStr === startDateStr) {
    return WEEKDAY_NAMES_PT[startDay];
  }

  const [ey, em, ed] = endDateStr.split("-").map(Number);
  const endDate = new Date(ey, em - 1, ed);
  const endDay = endDate.getDay();

  return `${WEEKDAY_SHORT_PT[startDay]} a ${WEEKDAY_SHORT_PT[endDay]}`;
};

const HolidaysModal = ({ open, onClose, whatsApp, onUpdate }) => {
  const [saving, setSaving] = useState(false);
  const [holidays, setHolidays] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // Form novo feriado
  const [newName, setNewName] = useState("");
  const [newStartDate, setNewStartDate] = useState("");
  const [newEndDate, setNewEndDate] = useState("");

  useEffect(() => {
    if (open && whatsApp) {
      loadHolidays();
    }
  }, [open, whatsApp]);

  const loadHolidays = () => {
    try {
      if (whatsApp?.holidays) {
        const parsed = JSON.parse(whatsApp.holidays);
        if (Array.isArray(parsed)) {
          setHolidays(parsed);
          return;
        }
      }
      setHolidays([]);
    } catch (e) {
      setHolidays([]);
    }
  };

  const todayStr = useMemo(() => getSaoPauloToday(), []);

  // Verifica se hoje é feriado ativo
  const todayHoliday = useMemo(() => {
    return holidays.find(h => {
      if (h.active === false) return false;
      const start = h.startDate;
      const end = h.endDate && h.endDate.trim() !== "" ? h.endDate : h.startDate;
      return todayStr >= start && todayStr <= end;
    });
  }, [holidays, todayStr]);

  const saveHolidaysToBackend = async (updatedList) => {
    setSaving(true);
    try {
      await api.put(`/whatsapp/${whatsApp.id}`, {
        holidays: JSON.stringify(updatedList)
      });
      setHolidays(updatedList);
      if (whatsApp) {
        whatsApp.holidays = JSON.stringify(updatedList);
      }
      toast.success("Feriados atualizados com sucesso!");
      if (onUpdate) onUpdate(updatedList);
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (index) => {
    const updated = [...holidays];
    updated[index].active = !updated[index].active;
    await saveHolidaysToBackend(updated);
  };

  const handleDeleteHoliday = async (index) => {
    const updated = holidays.filter((_, i) => i !== index);
    await saveHolidaysToBackend(updated);
  };

  const handleLoadYearHolidays = async () => {
    const yearHolidays = getBrazilHolidaysForYear(Number(selectedYear));
    const existingKeys = new Set(holidays.map(h => `${h.startDate}_${h.name}`));
    const toAdd = yearHolidays.filter(d => !existingKeys.has(`${d.startDate}_${d.name}`));

    let combined = [];
    if (holidays.length === 0) {
      combined = [...yearHolidays];
    } else {
      combined = [...holidays, ...toAdd];
    }

    // Ordena por startDate crescente
    combined.sort((a, b) => a.startDate.localeCompare(b.startDate));
    await saveHolidaysToBackend(combined);
  };

  const handleAddHoliday = async (e) => {
    e.preventDefault();
    if (!newName.trim() || !newStartDate) {
      toast.warning("Preencha o nome do feriado e a data de início.");
      return;
    }

    const newHolidayItem = {
      id: `custom_${Date.now()}`,
      name: newName.trim(),
      startDate: newStartDate,
      endDate: newEndDate || newStartDate,
      active: true
    };

    const combined = [...holidays, newHolidayItem];
    combined.sort((a, b) => a.startDate.localeCompare(b.startDate));

    await saveHolidaysToBackend(combined);
    setNewName("");
    setNewStartDate("");
    setNewEndDate("");
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        style: {
          height: "90vh",
          maxHeight: "90vh",
          borderRadius: 16,
          backgroundColor: "#f8fafc",
          display: "flex",
          flexDirection: "column"
        }
      }}
    >
      <DialogTitle
        style={{
          borderBottom: "1px solid #e2e8f0",
          backgroundColor: "#ffffff",
          padding: "16px 24px",
          flexShrink: 0
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <Typography variant="h6" style={{ fontWeight: 800, color: "#0f172a", display: "flex", alignItems: "center", gap: 8 }}>
              <BeachAccessIcon style={{ color: "#d97706" }} />
              Gestão de Feriados & Recessos
            </Typography>
            <Typography variant="caption" style={{ color: "#64748b" }}>
              Conexão: <strong>{whatsApp?.name}</strong> • A IA sabe quando é feriado, atende tirando dúvidas e orienta com carinho sobre o recesso.
            </Typography>
          </div>
          <IconButton onClick={onClose} size="small" style={{ color: "#64748b" }}>
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>

      <DialogContent style={{ padding: "20px 24px", flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", minHeight: 0 }}>
        {/* Card de Status de Hoje */}
        <Paper
          elevation={0}
          style={{
            padding: "14px 18px",
            borderRadius: 12,
            marginBottom: 16,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            backgroundColor: todayHoliday ? "#fef3c7" : "#f0fdf4",
            border: todayHoliday ? "1px solid #fde68a" : "1px solid #bbf7d0",
            flexShrink: 0
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ fontSize: "1.5rem" }}>{todayHoliday ? "🏖️" : "🟢"}</span>
            <div>
              <Typography style={{ fontWeight: 800, fontSize: "0.95rem", color: todayHoliday ? "#92400e" : "#166534" }}>
                {todayHoliday ? `Hoje é Feriado / Recesso: ${todayHoliday.name}` : "Hoje é um Dia Útil Normal"}
              </Typography>
              <Typography variant="caption" style={{ color: todayHoliday ? "#b45309" : "#15803d" }}>
                {todayHoliday
                  ? "A IA já sabe que não há equipe humana hoje e avisará aos pacientes cordialmente ao transferir ou tirar dúvidas."
                  : "A IA segue os horários normais de atendimento configurados na conexão."}
              </Typography>
            </div>
          </div>
          <Chip
            size="small"
            label={todayHoliday ? "IA em Modo Feriado" : "Expediente Padrão"}
            style={{
              fontWeight: 700,
              backgroundColor: todayHoliday ? "#f59e0b" : "#22c55e",
              color: "#ffffff"
            }}
          />
        </Paper>

        {/* Barra de Ações Rápidas por Ano */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 8, flexShrink: 0 }}>
          <Typography style={{ fontWeight: 700, fontSize: "0.9rem", color: "#1e293b" }}>
            📅 Calendário de Feriados ({holidays.filter(h => h.active).length} ativos de {holidays.length})
          </Typography>

          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <FormControl variant="outlined" size="small" style={{ minWidth: 90 }}>
              <Select
                value={selectedYear}
                onChange={e => setSelectedYear(e.target.value)}
                style={{ fontSize: "0.82rem", fontWeight: 700, height: 34, backgroundColor: "#ffffff" }}
              >
                <MenuItem value={2026}>2026</MenuItem>
                <MenuItem value={2027}>2027</MenuItem>
                <MenuItem value={2028}>2028</MenuItem>
              </Select>
            </FormControl>

            <Button
              size="small"
              variant="outlined"
              onClick={handleLoadYearHolidays}
              disabled={saving}
              startIcon={<EventAvailableIcon />}
              style={{
                textTransform: "none",
                fontSize: "0.78rem",
                fontWeight: 700,
                color: "#d97706",
                borderColor: "#fde68a",
                backgroundColor: "#fffbeb",
                borderRadius: 8,
                height: 34
              }}
            >
              🇧🇷 Carregar Feriados ({selectedYear})
            </Button>
          </div>
        </div>

        {/* Tabela de Feriados com Altura Expandida */}
        <Paper elevation={0} style={{ borderRadius: 12, border: "1px solid #e2e8f0", overflow: "hidden", marginBottom: 16, backgroundColor: "#ffffff", flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
          <div style={{ flex: 1, overflowY: "auto" }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell style={{ fontWeight: 700, color: "#475569", width: 70, whiteSpace: "nowrap", backgroundColor: "#f8fafc" }}>Ativo</TableCell>
                  <TableCell style={{ fontWeight: 700, color: "#475569", width: 220, minWidth: 220, whiteSpace: "nowrap", backgroundColor: "#f8fafc" }}>Data / Período</TableCell>
                  <TableCell style={{ fontWeight: 700, color: "#475569", width: 150, minWidth: 150, whiteSpace: "nowrap", backgroundColor: "#f8fafc" }}>Dia da Semana</TableCell>
                  <TableCell style={{ fontWeight: 700, color: "#475569", whiteSpace: "nowrap", backgroundColor: "#f8fafc" }}>Nome / Motivo do Feriado</TableCell>
                  <TableCell align="center" style={{ fontWeight: 700, color: "#475569", width: 70, whiteSpace: "nowrap", backgroundColor: "#f8fafc" }}>Excluir</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {holidays.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" style={{ padding: "36px 0", color: "#94a3b8", whiteSpace: "nowrap" }}>
                      Nenhum feriado cadastrado. Clique em <strong>"Carregar Feriados ({selectedYear})"</strong> acima ou adicione datas abaixo.
                    </TableCell>
                  </TableRow>
                ) : (
                  holidays.map((holiday, idx) => {
                    const isCurrent = todayHoliday && (todayHoliday.id === holiday.id || (todayHoliday.startDate === holiday.startDate && todayHoliday.name === holiday.name));
                    const isPeriod = holiday.endDate && holiday.endDate !== holiday.startDate;

                    return (
                      <TableRow
                        key={holiday.id || `${holiday.startDate}_${idx}`}
                        style={{
                          backgroundColor: isCurrent ? "#fef3c7" : holiday.active ? "#ffffff" : "#f1f5f9",
                          opacity: holiday.active ? 1 : 0.6
                        }}
                      >
                        <TableCell style={{ whiteSpace: "nowrap" }}>
                          <Switch
                            size="small"
                            checked={holiday.active !== false}
                            onChange={() => handleToggleActive(idx)}
                            color="primary"
                          />
                        </TableCell>
                        <TableCell style={{ fontWeight: 600, color: "#334155", whiteSpace: "nowrap" }}>
                          {formatDateBR(holiday.startDate)}
                          {isPeriod && ` até ${formatDateBR(holiday.endDate)}`}
                          {isCurrent && (
                            <Chip
                              size="small"
                              label="HOJE"
                              style={{
                                marginLeft: 8,
                                height: 18,
                                fontSize: "0.65rem",
                                fontWeight: 800,
                                backgroundColor: "#f59e0b",
                                color: "#fff"
                              }}
                            />
                          )}
                        </TableCell>
                        <TableCell style={{ whiteSpace: "nowrap" }}>
                          <span
                            style={{
                              display: "inline-block",
                              padding: "2px 8px",
                              borderRadius: 6,
                              fontSize: "0.76rem",
                              fontWeight: 700,
                              whiteSpace: "nowrap",
                              backgroundColor: isCurrent ? "#fde68a" : "#f1f5f9",
                              color: isCurrent ? "#92400e" : "#475569"
                            }}
                          >
                            {getWeekdayLabel(holiday.startDate, holiday.endDate)}
                          </span>
                        </TableCell>
                        <TableCell style={{ fontWeight: 600, color: holiday.active ? "#0f172a" : "#64748b", whiteSpace: "nowrap" }}>
                          {holiday.name}
                        </TableCell>
                        <TableCell align="center" style={{ whiteSpace: "nowrap" }}>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteHoliday(idx)}
                            style={{ color: "#ef4444" }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </Paper>

        {/* Formulário Novo Feriado */}
        <Paper
          elevation={0}
          style={{
            padding: "14px 18px",
            borderRadius: 12,
            border: "1px solid #e2e8f0",
            backgroundColor: "#ffffff",
            flexShrink: 0
          }}
        >
          <Typography style={{ fontWeight: 700, fontSize: "0.86rem", color: "#1e293b", marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
            <AddIcon style={{ fontSize: "1.1rem", color: "#2563eb" }} />
            Adicionar Novo Feriado ou Recesso Municipal / Customizado
          </Typography>
          <form onSubmit={handleAddHoliday}>
            <div style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr 1fr auto", gap: 12, alignItems: "center" }}>
              <TextField
                size="small"
                variant="outlined"
                label="Nome do Feriado / Motivo"
                placeholder="Ex: Aniversário da Cidade / Recesso"
                value={newName}
                onChange={e => setNewName(e.target.value)}
                required
              />
              <TextField
                size="small"
                variant="outlined"
                label="Data Inicial"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={newStartDate}
                onChange={e => setNewStartDate(e.target.value)}
                required
              />
              <TextField
                size="small"
                variant="outlined"
                label="Data Final (opcional)"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={newEndDate}
                onChange={e => setNewEndDate(e.target.value)}
              />
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={saving}
                style={{
                  height: 40,
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: 8,
                  backgroundColor: "#2563eb",
                  color: "#ffffff"
                }}
              >
                {saving ? <CircularProgress size={20} color="inherit" /> : "+ Adicionar"}
              </Button>
            </div>
          </form>
        </Paper>
      </DialogContent>

      <DialogActions style={{ padding: "12px 24px", borderTop: "1px solid #e2e8f0", backgroundColor: "#ffffff", flexShrink: 0 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          style={{ textTransform: "none", fontWeight: 600, borderRadius: 8 }}
        >
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HolidaysModal;
